import { useCallback, useEffect, useRef, useState } from 'react';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import {
  getSessionCounts,
  getTagCatalog,
  listSessionsByFolder,
  searchSessions,
} from '@/api/sessions';
import { useAuthStore } from '@/stores/authStore';
import { useConversationStore } from '@/stores/conversationStore';
import { mapFolderPage } from '@/utils/mapSession';
import type { EscalationItemDto, FolderId, PendingEmailDraftDto } from '@/types';
import { DEFAULT_FOLDER, isListFolder } from './folders';
import { createRowRefresher, createSequencer, useRowRefreshStore, type RowRefreshOutcome, type RowRefresher, type Sequencer } from './rowRefresh';
import { scopeKey } from './viewer';

/** A burst of "counts may have moved" pushes becomes ONE counts read this long after the last one. */
export const COUNTS_DEBOUNCE_MS = 1000;
/** Same for "the tag catalog may have changed". */
export const CATALOG_DEBOUNCE_MS = 1000;
/** Re-selecting a folder whose first page is older than this reloads it - and so does the 60 s poll (`refreshSelectedFolder`). */
export const FOLDER_STALE_MS = 30_000;
/** Scrolled-list poll refresh re-reads at most this many listed rows per tick (two pages of 20). */
export const SCROLLED_REFRESH_MAX_ROWS = 40;

/**
 * Run `run` with at most ONE in flight. A trigger that arrives while it is running does not start a second one: it marks
 * the run dirty, and exactly one more follows the current one (so the answer is never older than the last trigger).
 */
function singleFlight(run: () => Promise<void>): () => Promise<void> {
  let running: Promise<void> | null = null;
  let dirty = false;
  return () => {
    if (running) {
      dirty = true;
      return running;
    }
    running = (async () => {
      try {
        do {
          dirty = false;
          await run();
        } while (dirty);
      } finally {
        running = null;
      }
    })();
    return running;
  };
}

/**
 * ADR-033 / PR-C2 - everything the shell loads, and how it keeps it honest:
 *
 *  - the PER-FOLDER keyset list (first page on selection, next page on `loadMore`, 20 rows, de-duplicated by
 *    `sessionId` in the store, the cursor taken from the envelope of THAT folder, never a refetch of page 1 while a page
 *    is loading, never a second concurrent `loadMore`);
 *  - the folder COUNTS, the tag CATALOG, the DRAFTS feed (the drafts folder has no list call, OP-582) and the open
 *    CONSULTATIONS feed;
 *  - the ROW REFRESH queue (`refreshRow`): the only way the console re-reads one conversation. 200 = upsert + un-hide;
 *    404 = hide (the server will not show it to this viewer); anything else leaves the row exactly as it was.
 *
 * <b>Latest request wins.</b> Every list page, row read, counts read and catalog read takes a sequence number per target
 * when it is SENT; an answer older than the newest one already applied for that target is dropped (rowRefresh.ts).
 * <b>Per tenant + agent.</b> An answer that lands after the signed-in tenant or agent changed is dropped, and the
 * sequence numbers, the in-flight markers and the tag-key memory are wiped with the stores.
 *
 * The console never decides ownership here: it asks the server and applies what it says. This hook owns no UI state
 * beyond `selectedFolder` and the initial-load flag; every row lives in conversationStore.
 */
export function useSessionLoaders() {
  const seqRef = useRef<Sequencer>(createSequencer());
  const seq = seqRef.current;

  const [selectedFolder, setSelectedFolder] = useState<FolderId>(DEFAULT_FOLDER);
  const selectedRef = useRef<FolderId>(DEFAULT_FOLDER);
  const [sessionsLoading, setSessionsLoading] = useState(true);

  const mountedRef = useRef(true);
  const folderInflightRef = useRef(new Map<string, number>());
  // The folders the agent has scrolled PAST page 1 of (a `loadMore` was applied since the folder's last first-page load).
  // The poll must never replace those lists - that would drop pages under the scroll position - so it re-reads the listed rows instead.
  const deepFoldersRef = useRef(new Set<string>());

  // ── Counts ────────────────────────────────────────────────────────────────────────────────────────────────────
  const countsRunRef = useRef<() => Promise<void>>();
  if (!countsRunRef.current) {
    countsRunRef.current = singleFlight(async () => {
      const scope = scopeKey();
      const n = seq.next('counts');
      try {
        const counts = await getSessionCounts(true);
        if (scope !== scopeKey() || !seq.accept('counts', n)) return;
        // The drafts count is the drafts FEED's length (the counts endpoint does not know drafts): keep what the feed said.
        const prev = useConversationStore.getState().folderCounts;
        useConversationStore.getState().setFolderCounts('drafts' in prev ? { ...counts, drafts: prev.drafts } : counts);
      } catch {
        /* keep the counts the rail has; the 60 s poll and the next push try again */
      }
    });
  }
  /** Read the counts now (boot, reconnect, the poll): one request in flight, a trigger during it queues exactly one more. */
  const refreshCounts = useCallback((): Promise<void> => countsRunRef.current!(), []);

  const countsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** "The counts may have moved" (a push): debounced, so a burst is ONE read. */
  const requestCounts = useCallback(() => {
    if (countsTimerRef.current) clearTimeout(countsTimerRef.current);
    countsTimerRef.current = setTimeout(() => {
      countsTimerRef.current = null;
      void refreshCounts();
    }, COUNTS_DEBOUNCE_MS);
  }, [refreshCounts]);

  // ── Row refresh queue ─────────────────────────────────────────────────────────────────────────────────────────
  // ONE queue (rowRefresh.ts `createRowRefresher`): ids asked for within 300 ms share one request, four reads at a time,
  // 200 = upsert + un-hide, 404 = hide, anything else leaves the row as it was.
  const refresherRef = useRef<RowRefresher>();
  if (!refresherRef.current) refresherRef.current = createRowRefresher({ seq });
  /**
   * Re-read ONE conversation from the server and apply the answer. The row is marked "being re-read" from THIS call
   * until the answer is applied, which is what the chat view's "Confirming who owns this conversation" state is.
   * Resolves with how it ended; never rejects.
   */
  const refreshRow = useCallback((sessionId: string): Promise<RowRefreshOutcome> => refresherRef.current!.refreshRow(sessionId), []);

  /** Re-read every row the console only knows from a push or a draft placeholder (`partial`): they have no owner, no folder. */
  const refreshPartialRows = useCallback(() => {
    const { conversations } = useConversationStore.getState();
    for (const c of conversations) if (c.partial === true) void refreshRow(c.sessionId);
  }, [refreshRow]);

  // ── Drafts and consultations ──────────────────────────────────────────────────────────────────────────────────
  const draftsRunRef = useRef<() => Promise<void>>();
  if (!draftsRunRef.current) {
    draftsRunRef.current = singleFlight(async () => {
      const scope = scopeKey();
      const n = seq.next('drafts');
      try {
        // ADR-019 / OP-582: the drafts folder has NO list call - its rows are the sessions this feed names. Best-effort: a
        // failure leaves what the live push path already put on the rows.
        const resp = await axiosClient.get<PendingEmailDraftDto[]>(API.AI.EMAIL_DRAFTS_PENDING);
        if (scope !== scopeKey() || !seq.accept('drafts', n)) return;
        const feed = Array.isArray(resp.data) ? resp.data : [];
        const store = useConversationStore.getState();
        store.hydratePendingDrafts(feed, useAuthStore.getState().user?.agentId);
        // The drafts count is the length of the feed. Written directly (not through setFolderCounts) so it never claims
        // that the folder COUNTS have loaded.
        useConversationStore.setState({ folderCounts: { ...useConversationStore.getState().folderCounts, drafts: feed.length } });
      } catch {
        /* non-blocking */
      } finally {
        // A draft whose session was not on any loaded page became a placeholder row, and a push may have left one too:
        // read the real ones. OUTSIDE the feed's success path on purpose - a failing (or dropped) drafts read must never
        // strand a placeholder that has no owner and no folder.
        if (mountedRef.current && scope === scopeKey()) refreshPartialRows();
      }
    });
  }
  const refreshDrafts = useCallback((): Promise<void> => draftsRunRef.current!(), []);

  const consultationsRunRef = useRef<() => Promise<void>>();
  if (!consultationsRunRef.current) {
    consultationsRunRef.current = singleFlight(async () => {
      const scope = scopeKey();
      const n = seq.next('consultations');
      try {
        // OP-957: a consultation raised before this console opened used to be invisible. Best-effort, like the drafts.
        const resp = await axiosClient.get<EscalationItemDto[]>(API.ESCALATION.QUEUE);
        if (scope !== scopeKey() || !seq.accept('consultations', n)) return;
        useConversationStore
          .getState()
          .hydrateOpenConsultations(Array.isArray(resp.data) ? resp.data : [], useAuthStore.getState().user?.agentId);
      } catch {
        /* non-blocking */
      }
    });
  }
  const refreshConsultations = useCallback((): Promise<void> => consultationsRunRef.current!(), []);

  // ── Per-folder keyset list ────────────────────────────────────────────────────────────────────────────────────
  /**
   * Load one page of one folder.
   *  - `drafts`: no list call; the drafts feed is read instead.
   *  - `search:<q>`: the keyset search.
   *  - otherwise `GET /api/sessions?folder=&cursor=`.
   * `append` sends the folder's own cursor and is ignored when there is no next page or a request for the folder is
   * already running. A plain `replace` is ignored while the folder is loading (a page is never refetched under itself);
   * `force` (reconnect, "restored", a claim) always sends - the older answer then loses to the newer one.
   * Resolves when the attempt ends; never rejects.
   */
  const loadFolder = useCallback(
    async (folder: FolderId, mode: 'replace' | 'append' = 'replace', opts: { force?: boolean } = {}): Promise<void> => {
      if (!folder) return;
      if (folder === 'drafts') {
        await refreshDrafts();
        return;
      }
      const store = useConversationStore.getState();
      const inflight = folderInflightRef.current;
      const running = inflight.get(folder) ?? 0;

      let cursor: string | undefined;
      if (mode === 'append') {
        const page = store.folderPages[folder];
        if (!page || !page.hasMore || !page.cursor) return;
        if (running > 0) return;
        cursor = page.cursor;
      } else if (running > 0 && opts.force !== true) {
        return;
      }

      const isSearch = folder.startsWith('search:');
      const query = isSearch ? folder.slice('search:'.length) : '';
      if (isSearch && !query.trim()) return;

      const scope = scopeKey();
      const requestedAt = Date.now();
      const target = `folder:${folder}`;
      const n = seq.next(target);
      inflight.set(folder, running + 1);
      store.setFolderLoading(folder, true);

      try {
        const resp = isSearch ? await searchSessions(query, cursor) : await listSessionsByFolder(folder, cursor);
        if (scope !== scopeKey() || !seq.accept(target, n)) return;
        const page = mapFolderPage(resp);
        useConversationStore
          .getState()
          .applyFolderPage(folder, page.conversations, page.nextCursor, page.hasMore, requestedAt, mode);
        if (mode === 'append') deepFoldersRef.current.add(folder);
        else deepFoldersRef.current.delete(folder);
        // A list row carries no consultation / draft decoration of its own beyond what the store carries across: read
        // the (fresh) consultation queue so a new row is decorated.
        void refreshConsultations();
      } catch {
        if (scope !== scopeKey()) return;
        // An older request failing after a newer one was applied says nothing about the folder; only the newest does.
        if (!seq.isCurrent(target, n)) return;
        if ((inflight.get(folder) ?? 0) <= 1) useConversationStore.getState().setFolderError(folder);
      } finally {
        const left = Math.max(0, (inflight.get(folder) ?? 1) - 1);
        if (left === 0) inflight.delete(folder);
        else inflight.set(folder, left);
        if (scope === scopeKey()) {
          // applyFolderPage / setFolderError already cleared `loading`; a DROPPED answer did not, and an answer that
          // landed while another request is still running must leave the flag on.
          const page = useConversationStore.getState().folderPages[folder];
          if (page && page.loading !== left > 0) useConversationStore.getState().setFolderLoading(folder, left > 0);
        }
      }
    },
    [refreshDrafts, refreshConsultations, seq],
  );

  /** The next page of the selected folder. Ignored with no next page, or while a request for the folder is running. */
  const loadMore = useCallback((): Promise<void> => loadFolder(selectedRef.current, 'append'), [loadFolder]);

  /** Reload the selected folder's first page now (a claim, a restore, `reloadSessions` callers). */
  const reloadSelected = useCallback(async (): Promise<void> => {
    await loadFolder(selectedRef.current, 'replace', { force: true });
    requestCounts();
  }, [loadFolder, requestCounts]);

  /**
   * The 60 s poll's list refresh. Without it the counts (re-read by the poll) and the SELECTED folder's rows (loaded once,
   * on selection) drift apart whenever no push arrives - which is every tenant while only PR-A1 is deployed - and the
   * badge and the rows disagree until the agent re-selects the folder.
   *
   *  - only a LIST folder: `drafts` has no list call (the poll already re-reads the drafts feed) and a search is a snapshot
   *    of a query the agent typed - neither is replaced behind their back;
   *  - only when the folder's first page is older than {@link FOLDER_STALE_MS} (never loaded counts as older) and no
   *    request for it is already running;
   *  - the agent is still on page 1: replace it (`force`, so an older answer in flight loses to this one);
   *  - the agent has scrolled PAST page 1: keep every page under the scroll position and re-read the LISTED ROWS one by
   *    one instead (`refreshRow`: a row that left the viewer's sight answers 404 and is hidden, a changed row is merged).
   * Never rejects; resolves when the list reload ends (the per-row reads are not waited for: the poll must not be held up).
   */
  const refreshSelectedFolder = useCallback(async (): Promise<void> => {
    const folder = selectedRef.current;
    if (!folder || !isListFolder(folder)) return;
    const page = useConversationStore.getState().folderPages[folder];
    if (page?.loading) return;
    const stale = !page || page.loadedAt === null || Date.now() - page.loadedAt > FOLDER_STALE_MS;
    if (!stale) return;
    if (page && page.ids.length > 0 && deepFoldersRef.current.has(folder)) {
      // Load cap (coordinator, PR-C2 round 3): one GET per listed row per tick is unbounded for a deep scroll
      // (a five-page list is ~100 detail reads a minute). The first two pages are the rows the agent is
      // looking at or just left; anything deeper is re-read on the next select, reconnect or push.
      for (const id of page.ids.slice(0, SCROLLED_REFRESH_MAX_ROWS)) void refreshRow(id);
      return;
    }
    await loadFolder(folder, 'replace', { force: true });
  }, [loadFolder, refreshRow]);

  /** Select a folder (the URL owner pushes this in). Loads its first page when it has none yet or it is older than 30 s. */
  const selectFolder = useCallback(
    (folder: FolderId) => {
      selectedRef.current = folder;
      setSelectedFolder(folder);
      if (folder === 'drafts') {
        void refreshDrafts();
        return;
      }
      const page = useConversationStore.getState().folderPages[folder];
      const stale = !page || page.loadedAt === null || Date.now() - page.loadedAt > FOLDER_STALE_MS;
      if (stale) void loadFolder(folder, 'replace');
    },
    [loadFolder, refreshDrafts],
  );

  // ── Tag catalog ───────────────────────────────────────────────────────────────────────────────────────────────
  const catalogLoadedRef = useRef(false);
  const askedTagKeysRef = useRef(new Set<string>());
  const catalogRunRef = useRef<() => Promise<void>>();
  if (!catalogRunRef.current) {
    catalogRunRef.current = singleFlight(async () => {
      const scope = scopeKey();
      const n = seq.next('catalog');
      try {
        // Active AND inactive: a chip on an old row must still resolve to its label.
        const tags = await getTagCatalog(true);
        if (scope !== scopeKey() || !seq.accept('catalog', n)) return;
        const store = useConversationStore.getState();
        store.setTagCatalog(tags);
        catalogLoadedRef.current = true;
        // Whatever is STILL unknown after this read stays unknown (a deleted tag): do not ask again for those keys.
        const known = new Set(tags.map((t) => t.key));
        for (const c of useConversationStore.getState().conversations) {
          for (const k of c.tags ?? []) if (!known.has(k)) askedTagKeysRef.current.add(k);
        }
      } catch {
        /* keep the catalog the console has */
      }
    });
  }
  const loadCatalog = useCallback((): Promise<void> => catalogRunRef.current!(), []);

  const catalogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** "The catalog may have changed" (a push, or a row carries a key it lacks): debounced into ONE read. */
  const requestCatalog = useCallback(() => {
    if (catalogTimerRef.current) clearTimeout(catalogTimerRef.current);
    catalogTimerRef.current = setTimeout(() => {
      catalogTimerRef.current = null;
      void loadCatalog();
    }, CATALOG_DEBOUNCE_MS);
  }, [loadCatalog]);

  useEffect(() => {
    const unsubscribe = useConversationStore.subscribe((state, prev) => {
      // A row the console only knows from a PUSH (a message or a session-created for a conversation it has not loaded) is
      // a placeholder: it has no owner and no folder, so the folder views keep it out until the server has described it.
      // Read it AS SOON AS it appears - waiting for the next drafts read or the 60 s poll would leave a brand-new
      // conversation (or one that just moved above the cursor of an unfinished page) missing from its list while the
      // debounced counts already include it. A burst coalesces in the row queue (300 ms batch, one read per id).
      if (state.conversations !== prev.conversations) {
        const before = new Set<string>();
        for (const c of prev.conversations) if (c.partial === true) before.add(c.sessionId);
        for (const c of state.conversations) {
          if (c.partial === true && !before.has(c.sessionId)) void refreshRow(c.sessionId);
        }
      }
      // ReceiveTagCatalogChanged bumped the version.
      if (state.catalogVersion !== prev.catalogVersion) requestCatalog();
      // A row carries a tag key the catalog does not have (a tag created since the last read): ask once per such key.
      if (state.conversations !== prev.conversations && catalogLoadedRef.current) {
        const known = new Set(state.tagCatalog.map((t) => t.key));
        let unknown = false;
        for (const c of state.conversations) {
          for (const k of c.tags ?? []) {
            if (known.has(k) || askedTagKeysRef.current.has(k)) continue;
            askedTagKeysRef.current.add(k);
            unknown = true;
          }
        }
        if (unknown) requestCatalog();
      }
    });
    return unsubscribe;
  }, [requestCatalog, refreshRow]);

  // ── Boot, reconnect, scope ────────────────────────────────────────────────────────────────────────────────────
  /** Everything the console needs on first sight (parallel), then the real rows behind any placeholder. */
  const boot = useCallback((): Promise<void> => {
    const first = loadFolder(selectedRef.current, 'replace');
    void refreshCounts();
    void refreshDrafts();
    void refreshConsultations();
    void loadCatalog();
    return first.then(() => {
      if (mountedRef.current) refreshPartialRows();
    });
  }, [loadFolder, refreshCounts, refreshDrafts, refreshConsultations, loadCatalog, refreshPartialRows]);

  /**
   * The hub came back: every push sent while it was down is LOST. Forget the folder pages (the open conversation's row
   * stays pinned, OP-372), then reload the selected folder's first page, the counts, the drafts, the consultations, the
   * catalog and the open row.
   */
  const reconnect = useCallback(() => {
    const store = useConversationStore.getState();
    const active = store.activeSessionId;
    store.resetFolderPages();
    deepFoldersRef.current.clear(); // every page was forgotten: the first page is all there is again
    void loadFolder(selectedRef.current, 'replace', { force: true });
    void refreshCounts();
    void refreshDrafts();
    void refreshConsultations();
    void loadCatalog();
    if (active) void refreshRow(active);
  }, [loadFolder, refreshCounts, refreshDrafts, refreshConsultations, loadCatalog, refreshRow]);

  const scope = useAuthStore((s) => `${s.user?.tenantId ?? ''}|${s.user?.agentId ?? ''}`);
  const bootedScopeRef = useRef<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    if (bootedScopeRef.current !== null && bootedScopeRef.current !== scope) {
      // A different tenant or agent: nothing learned for the previous one may be compared with, or shown to, this one.
      seq.reset();
      useRowRefreshStore.getState().clear();
      folderInflightRef.current.clear();
      deepFoldersRef.current.clear();
      askedTagKeysRef.current.clear();
      catalogLoadedRef.current = false;
    }
    bootedScopeRef.current = scope;
    setSessionsLoading(true);
    let cancelled = false;
    void boot().finally(() => {
      if (!cancelled) setSessionsLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // `boot` is stable by construction; the effect is keyed on the signed-in scope only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // Unmount: stop the timers and settle everything still waiting, so nothing is left "being re-read" for ever.
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (countsTimerRef.current) clearTimeout(countsTimerRef.current);
      if (catalogTimerRef.current) clearTimeout(catalogTimerRef.current);
      refresherRef.current?.dispose();
    };
  }, []);

  return {
    selectedFolder,
    sessionsLoading,
    loadFolder,
    loadMore,
    reloadSelected,
    refreshSelectedFolder,
    selectFolder,
    refreshRow,
    refreshCounts,
    requestCounts,
    refreshDrafts,
    refreshConsultations,
    loadCatalog,
    requestCatalog,
    refreshPartialRows,
    reconnect,
  };
}
